import { useChatLoading, useCurrentAgent } from '@/features/chats/stores';
import { getChatStores } from '@/features/chats/lib/chat-store-bindings';
import { useMemo } from 'react';
import { type CategoryId } from '@/shared/constants/category-colors';
import { CATEGORY_GREETINGS_INFO } from '@/shared/constants/category-greetings';
import { useTranslation } from 'react-i18next';
import { AgentIntro, agentIntroLook } from '@/entities/agent';

/**
 * Loose mirror of the tool-categories query result shape consumed by
 * Intro. Kept narrow so this slice does not import the concrete
 * `useToolCategories` hook from `@/widgets/app-shell/...` (FSD layer
 * violation — features MUST NOT import from widgets).
 *
 * `data` is optional + `unknown[]`-bearing on purpose — the concrete
 * TanStack `UseQueryResult` returns `undefined` while loading, and
 * extra category fields on the source shape (like `tools`) are widened
 * to a structural subset so the consuming page can pass the unmodified
 * `useToolCategories` result without a wrapper.
 */
export interface IntroToolCategoriesDeps {
  useToolCategories: () => {
    data?: ReadonlyArray<{
      id: string;
      label: string;
      tools: ReadonlyArray<{ id: string }>;
    }>;
    isLoading: boolean;
  };
}

export interface IntroProps {
  /**
   * Phase D injection (CRITICAL/WARNING A-13). The consuming
   * page binds `useToolCategories` from the widgets layer and passes it
   * here so this slice can read agent categorization without violating
   * the features → widgets boundary.
   *
   * **REQUIRED** (Round 2 fix CRITICAL #3). Previously
   * optional with a `NOOP_TOOL_CATEGORIES_DEPS` default — `useToolCategories`
   * is invoked as a hook below, and the noop returns a constant
   * `{ data: [], isLoading: false }` (0 internal hooks) while the real
   * implementation calls `useQuery + useCallback + usePermissions +
   * useCurrentWorkspaceId` (4+ hooks). Toggling between the two
   * violates the Rules of Hooks. Required prop forces every consumer
   * to supply a stable deps bag (production via `pages/Root/Root.tsx`
   * with a module-level constant, tests via an explicit no-op stub).
   */
  toolCategoriesDeps: IntroToolCategoriesDeps;
}

export const Intro = ({ toolCategoriesDeps }: IntroProps) => {
  const { t } = useTranslation('chats');

  const isLoading = useChatLoading();
  const agent = useCurrentAgent();
  const { sidePanelLayoutMode: layoutMode } = getChatStores().uiStore.useSidePanelLayoutMode();
  const { data: categories = [], isLoading: isLoadingCategories } =
    toolCategoriesDeps.useToolCategories();

  const greetingInfo = useMemo(() => {
    if (!agent) return null;

    const category = categories.find((category) => {
      return category.tools.some((tool) => tool.id === agent.id);
    });

    const categoryId = category ? (category.id as CategoryId) : undefined;
    const look = agentIntroLook(agent.id, categoryId);

    // If agent is not assigned to any category, render the 'any' case fallback
    if (!categoryId || !category) {
      return {
        title: agent.title,
        description: agent.description || '',
        greeting: agent.greeting || '',
        ...look,
      };
    }

    // If agent is assigned to a category, but lacks custom greeting, render greetings based on category
    if (!agent.greeting) {
      return {
        title: category.label,
        description: CATEGORY_GREETINGS_INFO[categoryId].description,
        greeting: CATEGORY_GREETINGS_INFO[categoryId].greeting,
        icon: CATEGORY_GREETINGS_INFO[categoryId].icon,
        tagBackground: look.tagBackground,
      };
    }
    return {
      title: agent.title,
      description: agent.description,
      greeting: agent.greeting,
      ...look,
    };
  }, [agent, categories]);

  if (layoutMode === 'fullscreen' || !greetingInfo) {
    return null;
  }

  if (isLoading || isLoadingCategories) {
    return (
      <div className="grow flex items-center justify-center">
        <span className="type-body-lg text-foreground-subtle">{t('chats.intro.loading')}</span>
      </div>
    );
  }

  return (
    <AgentIntro
      icon={greetingInfo.icon}
      tagBackground={greetingInfo.tagBackground}
      title={greetingInfo.title}
      description={greetingInfo.description}
      greeting={greetingInfo.greeting}
    />
  );
};
